import * as React from "react";
import {
  useConvex,
  useMutation,
  usePaginatedQuery,
  useQuery,
} from "convex/react";
import {
  ArrowLeft,
  ArrowRight,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "$convex/convexApi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { formatCurrency, formatDate } from "@/lib/format";
import { hasPermission } from "@/lib/roles";
import { CampaignClientReview } from "./CampaignClientReview";
import { CampaignClientsTable } from "./CampaignClientsTable";
import type { SavingsCampaign, SavingsProspect } from "./types";

function errorMessage(error: unknown) {
  return error instanceof Error
    ? error.message
    : "The campaign could not be updated. Please try again.";
}

function CampaignWorkspace({
  campaignId,
  onBack,
}: {
  campaignId: string;
  onBack: () => void;
}) {
  const { userRole } = useAuthIdentity();
  const detail = useQuery(api.contractSavings.detail, { campaignId }) as
    | {
        campaign: SavingsCampaign;
        prospects: SavingsProspect[];
        disclaimer: string;
        canManage: boolean;
      }
    | undefined;
  const [selectedCustomer, setSelectedCustomer] = React.useState<string | null>(
    null,
  );
  const [addOpen, setAddOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [removing, setRemoving] = React.useState(false);
  const [removeTarget, setRemoveTarget] =
    React.useState<SavingsProspect | null>(null);
  const [windowMonths, setWindowMonths] = React.useState("");
  const [windowPreset, setWindowPreset] = React.useState<string | null>(null);
  const [refreshProgress, setRefreshProgress] = React.useState<string | null>(
    null,
  );
  const [preview, setPreview] = React.useState<SavingsProspect | undefined>();
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [retry, setRetry] = React.useState(0);
  const convex = useConvex();
  const updateWindow = useMutation(api.contractSavings.updateWindow);
  const saveCustomer = useMutation(api.contractSavings.saveCustomer);
  const removeCustomer = useMutation(api.contractSavings.removeCustomer);
  const { results, status, loadMore } = usePaginatedQuery(
    api.contractSavings.listCustomers,
    addOpen ? {} : "skip",
    { initialNumItems: 50 },
  );
  React.useEffect(() => {
    let current = true;
    setPreview(undefined);
    setPreviewError(null);
    if (selectedCustomer) {
      convex
        .query(api.contractSavings.previewCustomer, {
          campaignId,
          customerId: selectedCustomer,
        })
        .then((result: SavingsProspect) => {
          if (current) setPreview(result);
        })
        .catch((error: unknown) => {
          if (current) setPreviewError(errorMessage(error));
        });
    }
    return () => {
      current = false;
    };
  }, [
    convex,
    campaignId,
    selectedCustomer,
    detail?.campaign.lookbackMonths,
    retry,
  ]);
  if (!detail)
    return (
      <p role="status" className="py-12 text-sm text-muted-foreground">
        Loading campaign…
      </p>
    );
  const busy = saving || removing || refreshProgress !== null;
  const selectedWindowPreset =
    windowPreset ??
    ([6, 12].includes(detail.campaign.lookbackMonths)
      ? String(detail.campaign.lookbackMonths)
      : "custom");
  const selectedWindowMonths =
    windowPreset === null
      ? String(detail.campaign.lookbackMonths)
      : windowMonths;
  const eligible = detail.prospects.filter((p) => p.eligible && !p.stale);
  const savedProspect = detail.prospects.find(
    (p) => p.customerId === selectedCustomer,
  );
  const customers = results as {
    _id: string;
    name: string;
    email: string | null;
  }[];

  function review(customerId: string) {
    setAddOpen(false);
    setSelectedCustomer(customerId);
  }
  async function handleWindowChange(event: React.FormEvent) {
    event.preventDefault();
    if (!detail || busy) return;
    const months = Number(selectedWindowMonths);
    if (!Number.isInteger(months) || months < 1 || months > 24) return;
    setRefreshProgress("Updating purchase window…");
    try {
      await updateWindow({ campaignId, lookbackMonths: months });
      let failed = 0;
      for (let index = 0; index < detail.prospects.length; index++) {
        setRefreshProgress(
          `Recalculating client ${index + 1} of ${detail.prospects.length}…`,
        );
        try {
          await saveCustomer({
            campaignId,
            customerId: detail.prospects[index]!.customerId,
            onlyIfSaved: true,
          });
        } catch {
          failed++;
        }
      }
      if (failed)
        toast.error(
          `${failed} client estimates could not be refreshed. They remain marked for review.`,
        );
      else toast.success("Purchase window and saved client estimates updated.");
      setWindowMonths("");
      setWindowPreset(null);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setRefreshProgress(null);
    }
  }
  async function handleSave() {
    if (!selectedCustomer || busy) return;
    setSaving(true);
    try {
      await saveCustomer({ campaignId, customerId: selectedCustomer });
      toast.success("Client estimate saved to the draft.");
      setSelectedCustomer(null);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  }
  async function handleRemove() {
    if (!removeTarget || busy) return;
    setRemoving(true);
    try {
      await removeCustomer({ campaignId, customerId: removeTarget.customerId });
      toast.success("Client removed from the campaign list.");
      setRemoveTarget(null);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setRemoving(false);
    }
  }
  return (
    <div className="space-y-5">
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2"
        onClick={onBack}
        disabled={busy}
      >
        <ArrowLeft aria-hidden="true" />
        All campaigns
      </Button>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-balance">
              {detail.campaign.name}
            </h1>
            <Badge variant="secondary">Draft · No emails sent</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Top two coffees by pounds ·{" "}
            {formatDate(detail.campaign.periodStart)} –{" "}
            {formatDate(detail.campaign.periodEnd)}
          </p>
        </div>
        {detail.canManage && (
          <Button onClick={() => setAddOpen(true)} disabled={busy}>
            <Plus aria-hidden="true" />
            Add clients
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 rounded-lg border bg-muted/20 px-4 py-3 text-sm">
        <span>
          <strong className="tabular-nums">{detail.prospects.length}</strong>{" "}
          <span className="text-muted-foreground">clients</span>
        </span>
        <span>
          <strong className="tabular-nums">{eligible.length}</strong>{" "}
          <span className="text-muted-foreground">estimates ready</span>
        </span>
        <span>
          <strong className="tabular-nums">
            {formatCurrency(
              eligible.reduce((sum, p) => sum + p.estimatedSavings!, 0),
            )}
          </strong>{" "}
          <span className="text-muted-foreground">estimated savings</span>
        </span>
      </div>
      {detail.canManage && (
        <form
          onSubmit={handleWindowChange}
          className="flex flex-wrap items-center gap-2"
        >
          <label htmlFor="campaign-window" className="mr-1 text-sm font-medium">
            Purchase history
          </label>
          <NativeSelect
            id="campaign-window"
            value={selectedWindowPreset}
            disabled={busy}
            onChange={(event) => {
              setWindowPreset(event.target.value);
              setWindowMonths(
                event.target.value === "custom"
                  ? String(detail.campaign.lookbackMonths)
                  : event.target.value,
              );
            }}
          >
            <option value="6">Last 6 months</option>
            <option value="12">Last 12 months</option>
            <option value="custom">Custom months</option>
          </NativeSelect>
          {selectedWindowPreset === "custom" && (
            <Input
              aria-label="Months (1–24)"
              className="w-24"
              type="number"
              min={1}
              max={24}
              step={1}
              required
              value={selectedWindowMonths}
              disabled={busy}
              onChange={(e) => setWindowMonths(e.target.value)}
            />
          )}
          <Button
            type="submit"
            variant="outline"
            disabled={
              busy ||
              !selectedWindowMonths ||
              Number(selectedWindowMonths) === detail.campaign.lookbackMonths ||
              !Number.isInteger(Number(selectedWindowMonths)) ||
              Number(selectedWindowMonths) < 1 ||
              Number(selectedWindowMonths) > 24
            }
          >
            <RefreshCw aria-hidden="true" className="size-4" />
            Apply & recalculate
          </Button>
          <span role="status" className="text-xs text-muted-foreground">
            {refreshProgress || "Completed deliveries only"}
          </span>
        </form>
      )}
      {detail.prospects.length ? (
        <CampaignClientsTable
          prospects={detail.prospects}
          onReview={review}
          onRemove={detail.canManage ? setRemoveTarget : undefined}
          disabled={busy}
        />
      ) : (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center">
          <Users
            className="size-8 text-muted-foreground"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <h2 className="font-medium">Build your campaign audience</h2>
          <p className="max-w-sm text-sm text-muted-foreground">
            Add a client to review their purchase history and three-month
            savings estimate.
          </p>
          {detail.canManage && (
            <Button variant="outline" onClick={() => setAddOpen(true)}>
              <Plus aria-hidden="true" />
              Add clients
            </Button>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Drafts only. Emails, response collection and sales-rep notifications are
        not enabled.
      </p>
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="gap-3 p-0 sm:max-w-lg">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle>Add clients</DialogTitle>
            <DialogDescription>
              Choose a client to review before adding them to this campaign.
            </DialogDescription>
          </DialogHeader>
          <Command>
            <CommandInput
              aria-label="Search clients to add"
              placeholder="Search loaded clients…"
            />
            <CommandList className="max-h-80">
              <CommandEmpty>
                {status === "LoadingFirstPage"
                  ? "Loading clients…"
                  : "No matching clients loaded. Load more or change your search."}
              </CommandEmpty>
              <CommandGroup>
                {customers.map((client) => (
                  <CommandItem
                    key={client._id}
                    value={`${client._id} ${client.name} ${client.email ?? ""}`}
                    onSelect={() => review(client._id)}
                    className="flex min-h-12 items-center justify-between gap-3 px-3"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {client.name}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {client.email || "Email required"}
                      </span>
                    </span>
                    {detail.prospects.some(
                      (p) => p.customerId === client._id,
                    ) ? (
                      <Badge variant="secondary">In campaign</Badge>
                    ) : (
                      <Plus
                        className="size-4 text-muted-foreground"
                        aria-hidden="true"
                      />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="flex items-center justify-between gap-3 border-t px-5 py-3">
            <span className="text-xs text-muted-foreground">
              {results.length} clients loaded
            </span>
            {status !== "Exhausted" && (
              <Button
                size="sm"
                variant="outline"
                disabled={status !== "CanLoadMore"}
                onClick={() => loadMore(50)}
              >
                {status === "LoadingMore" ? "Loading…" : "Load more clients"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Sheet
        open={selectedCustomer !== null}
        onOpenChange={(open) => {
          if (!open && !saving) setSelectedCustomer(null);
        }}
      >
        <SheetContent className="w-full gap-0 sm:max-w-3xl">
          <SheetHeader className="border-b p-5 pr-12">
            <SheetTitle>Review client</SheetTitle>
            <SheetDescription>
              Purchase history, estimated savings and message draft.
            </SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {selectedCustomer && hasPermission(userRole, "sales:view") && (
              <a
                className="mb-5 inline-flex min-h-10 items-center text-sm font-medium underline underline-offset-4"
                href={`/sales/analytics?clientId=${encodeURIComponent(selectedCustomer)}&comparisonCampaignId=${encodeURIComponent(campaignId)}`}
              >
                Choose purchase lines and replacement coffees in Sales Analytics
              </a>
            )}
            {previewError ? (
              <div role="alert" className="space-y-3">
                <p className="text-sm text-destructive">{previewError}</p>
                <Button
                  variant="outline"
                  onClick={() => setRetry((n) => n + 1)}
                >
                  Try again
                </Button>
              </div>
            ) : !preview || preview.customerId !== selectedCustomer ? (
              <p role="status" className="py-8 text-sm text-muted-foreground">
                Calculating purchase history and pricing…
              </p>
            ) : (
              <CampaignClientReview
                prospect={preview}
                disclaimer={detail.disclaimer}
                onSave={
                  detail.canManage && !refreshProgress ? handleSave : undefined
                }
                saving={saving}
                saved={Boolean(savedProspect)}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
      <AlertDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open && !removing) setRemoveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from this campaign list?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.customerName} will be removed from this campaign’s
              list along with their saved estimate. You can add them again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={busy}
              onClick={(event) => {
                event.preventDefault();
                void handleRemove();
              }}
            >
              <Trash2 aria-hidden="true" />
              {removing ? "Removing from list…" : "Remove from list"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function ContractSavingsCampaignsPage() {
  const { authUserId } = useAuthIdentity();
  const data = useQuery(api.contractSavings.list, authUserId ? {} : "skip") as
    { canManage: boolean; campaigns: SavingsCampaign[] } | undefined;
  const create = useMutation(api.contractSavings.create);
  const [campaignId, setCampaignId] = React.useState<string | null>(null);
  const [name, setName] = React.useState("");
  const [preset, setPreset] = React.useState("6");
  const [customMonths, setCustomMonths] = React.useState("9");
  const [creating, setCreating] = React.useState(false);
  const lookbackMonths = Number(preset === "custom" ? customMonths : preset);
  const validMonths =
    Number.isInteger(lookbackMonths) &&
    lookbackMonths >= 1 &&
    lookbackMonths <= 24;
  async function handleCreate(event: React.FormEvent) {
    event.preventDefault();
    if (creating || !name.trim() || !validMonths) return;
    setCreating(true);
    try {
      const id = await create({ name: name.trim(), lookbackMonths });
      setCampaignId(String(id));
      setName("");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCreating(false);
    }
  }
  if (campaignId)
    return (
      <CampaignWorkspace
        key={campaignId}
        campaignId={campaignId}
        onBack={() => setCampaignId(null)}
      />
    );
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Marketing
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-balance">
          Contract savings campaigns
        </h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Find each client’s two most-purchased coffees and review what a
          three-month commitment could save.
        </p>
      </div>
      <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm">
        <span className="font-medium">Draft workspace.</span> Creating a
        campaign or saving an estimate sends no emails.
      </div>
      {!data ? (
        <p role="status" className="py-8 text-sm text-muted-foreground">
          Loading campaigns…
        </p>
      ) : (
        <>
          {data.canManage && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Create a campaign</CardTitle>
                <CardDescription>
                  Choose the purchase window for this draft. Each campaign keeps
                  its own date range so estimates remain traceable.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  className="flex flex-wrap items-end gap-4"
                  onSubmit={handleCreate}
                >
                  <div className="min-w-60 flex-1">
                    <label
                      className="mb-2 block text-sm font-medium"
                      htmlFor="savings-name"
                    >
                      Campaign name
                    </label>
                    <Input
                      id="savings-name"
                      placeholder="e.g. Fall contract savings"
                      value={name}
                      maxLength={120}
                      required
                      onChange={(event) => setName(event.target.value)}
                    />
                  </div>
                  <div className="min-w-44">
                    <label
                      className="mb-2 block text-sm font-medium"
                      htmlFor="savings-lookback"
                    >
                      Purchase history
                    </label>
                    <NativeSelect
                      id="savings-lookback"
                      value={preset}
                      onChange={(event) => setPreset(event.target.value)}
                    >
                      <option value="6">Last 6 months</option>
                      <option value="12">Last 12 months</option>
                      <option value="custom">Custom months</option>
                    </NativeSelect>
                  </div>
                  {preset === "custom" && (
                    <div className="w-32">
                      <label
                        className="mb-2 block text-sm font-medium"
                        htmlFor="savings-custom"
                      >
                        Months (1–24)
                      </label>
                      <Input
                        id="savings-custom"
                        type="number"
                        min={1}
                        max={24}
                        step={1}
                        required
                        value={customMonths}
                        onChange={(event) =>
                          setCustomMonths(event.target.value)
                        }
                      />
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={creating || !name.trim() || !validMonths}
                  >
                    <Plus className="mr-2 size-4" aria-hidden="true" />
                    {creating ? "Creating…" : "Create draft"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Campaigns</CardTitle>
              <CardDescription>
                {data.campaigns.length} drafts · No campaigns have been sent
                from this workspace.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.campaigns.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-sm font-medium">
                    Your first campaign starts here
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Create a draft to review purchase history, estimates, and
                    client messages.
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {data.campaigns.map((campaign) => (
                    <button
                      key={campaign._id}
                      type="button"
                      className="flex min-h-16 w-full flex-wrap items-center justify-between gap-3 rounded-lg px-2 py-4 text-left outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => setCampaignId(campaign._id)}
                    >
                      <span>
                        <span className="block text-sm font-medium">
                          {campaign.name}
                        </span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {campaign.lookbackMonths} months ·{" "}
                          {formatDate(campaign.periodStart)} –{" "}
                          {formatDate(campaign.periodEnd)}
                        </span>
                      </span>
                      <span className="flex items-center gap-4">
                        <Badge variant="outline">Draft</Badge>
                        <ArrowRight
                          className="size-4 text-muted-foreground"
                          aria-hidden="true"
                        />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
